/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Grid } from "@mui/material";
import Button from "components/ArgonButton";
import Typography from "components/ArgonTypography";
import { Person, Groups, Payment, Add, Settings } from "@mui/icons-material";
import { Choice, NumberField, Section } from "./SMEControls";
import axios from "utils/api";
import { fetchAllPages } from "utils/fetchAllPages";
import { buildSMEPayload, days, idOf, nameOf, newAssignment, validateSME } from "./smeUtils";

function TutorRow({ row, index, onChange, onRemove, tutors, subjects }) {
  const [slots, setSlots] = useState([]);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setSlots([]);
    if (!row.tutorId) return undefined;
    onChange({ availabilityLoading: true, availabilityError: "" });
    Promise.all([
      fetchAllPages(axios, "/slots", { itemKeys: ["slots"] }),
      axios.get("/tutor", { params: { id: row.tutorId } }),
      axios.get(`/classes/get/active/classesFor/tutor/${row.tutorId}`, {
        params: { status: "ACTIVE" },
      }),
    ])
      .then(([allSlots, tutorResponse, response]) => {
        if (!active) return;
        const tutor =
          tutorResponse.data?.data?.tutor || tutorResponse.data?.data || tutorResponse.data?.tutor;
        const preferred = (tutor?.preferredTeachingSlots || []).map(idOf);
        setSlots(preferred.length ? allSlots.filter((s) => preferred.includes(idOf(s))) : allSlots);
        const sessions =
          response.data?.classes || response.data?.data?.classes || response.data?.data || [];
        onChange({
          availabilityLoading: false,
          busySessions: (Array.isArray(sessions) ? sessions : []).map((s) => ({
            ...s,
            slot:
              typeof s.finalisedActiveSlot === "object"
                ? s.finalisedActiveSlot
                : allSlots.find((slot) => idOf(slot) === idOf(s.finalisedActiveSlot)),
          })),
        });
      })
      .catch(() => {
        if (active)
          onChange({
            availabilityLoading: false,
            availabilityError: "Could not load tutor availability.",
          });
      });
    return () => {
      active = false;
    };
    // Each stable row owns its request; stale responses cannot update another tutor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.tutorId, retry]);
  const opts = (items) => items.map((v) => ({ value: idOf(v), label: nameOf(v) }));
  return (
    <Section
      title={`Tutor ${index + 1}`}
      description="Subjects, schedule and payment details"
      icon={<Groups />}
      action={
        <Button variant="outlined" size="small" color="error" onClick={onRemove}>
          Remove tutor
        </Button>
      }
    >
      <Grid container spacing={3} alignItems="flex-end">
        <Grid item xs={12} md={6}>
          <Choice
            label="Tutor"
            value={row.tutorId}
            options={opts(tutors)}
            onChange={(v) =>
              onChange({
                tutorId: v,
                subjects: [],
                finalisedActiveSlot: "",
                slot: null,
                busySessions: [],
                availabilityError: "",
                availabilityLoading: Boolean(v),
              })
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Choice
            label="Subjects"
            multiple
            value={row.subjects}
            options={opts(subjects)}
            onChange={(v) => onChange({ subjects: v })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Choice
            label="Class days"
            multiple
            value={row.classDays}
            options={days.map((label, value) => ({ label, value }))}
            onChange={(v) => onChange({ classDays: v })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Choice
            label="Time slot"
            value={row.finalisedActiveSlot}
            disabled={!row.tutorId || row.availabilityLoading}
            options={slots.map((s) => ({ value: idOf(s), label: `${s.startTime} – ${s.endTime}` }))}
            onChange={(v) =>
              onChange({ finalisedActiveSlot: v, slot: slots.find((s) => idOf(s) === v) })
            }
          />
        </Grid>
        {[
          ["duration", "Duration (minutes)"],
          ["allocatedClasses", "Allocated classes"],
          ["payRate", "Per Session Fee (T)"],
        ].map(([key, label]) => (
          <Grid item xs={12} md={6} key={key}>
            <NumberField
              fullWidth
              size="small"
              type="number"
              label={label}
              value={row[key]}
              inputProps={{ min: key === "payRate" ? 0 : 1 }}
              onChange={(e) => onChange({ [key]: e.target.value })}
            />
          </Grid>
        ))}
      </Grid>
      {row.availabilityLoading && <CircularProgress size={20} sx={{ mt: 2 }} />}
      {row.availabilityError && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry((v) => v + 1)}>Retry</Button>}
          sx={{ mt: 2 }}
        >
          {row.availabilityError}
        </Alert>
      )}
      {row.tutorId && !row.availabilityLoading && !row.availabilityError && !slots.length && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No slots available for this tutor.
        </Alert>
      )}
    </Section>
  );
}
export default function SMEForm({ form, setForm, parents, students, tutors, loading }) {
  const [subjects, setSubjects] = useState([]);
  const [subjectState, setSubjectState] = useState("loading");
  const [retry, setRetry] = useState(0);
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    let active = true;
    setSubjectState("loading");
    fetchAllPages(axios, "/subjects", { itemKeys: ["subjects"] })
      .then((items) => {
        if (active) {
          setSubjects(items);
          setSubjectState("ready");
        }
      })
      .catch(() => {
        if (active) setSubjectState("error");
      });
    return () => {
      active = false;
    };
  }, [retry]);
  const change = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };
  const updateRow = (localId, patch) => {
    setForm((prev) => ({
      ...prev,
      tutorAssignments: prev.tutorAssignments.map((r) =>
        r.localId === localId ? { ...r, ...patch } : r
      ),
    }));
  };
  const options = (items) => items.map((v) => ({ value: idOf(v), label: nameOf(v) }));
  const errors = validateSME(form);
  const payload = buildSMEPayload(form);
  return (
    <Box onBlur={() => setAttempted(true)}>
      <Alert severity="info" sx={{ mb: 3, fontSize: "14px", borderRadius: "8px" }}>
        SME saving coming soon. Class details cannot be saved yet.
      </Alert>
      {loading && <CircularProgress size={24} />}
      <Section
        title="Basic Information"
        description="Parent, student and class mode"
        icon={<Person />}
      >
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <Choice
              label="Parent"
              value={form.parentId}
              disabled={loading}
              options={options(parents)}
              onChange={(v) => change({ parentId: v, students: [] })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Choice
              label="Student"
              value={form.students[0] || ""}
              disabled={loading || !form.parentId}
              options={options(students)}
              onChange={(v) => change({ students: v ? [v] : [] })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Choice
              label="Mode"
              value={form.mode}
              options={["ONLINE", "OFFLINE", "BOTH"].map((v) => ({ value: v, label: v }))}
              onChange={(v) => change({ mode: v || "OFFLINE" })}
            />
          </Grid>
        </Grid>
      </Section>
      <Section title="Class Settings" description="Package duration and total classes" icon={<Settings />}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <NumberField
              label="Total Number of Months"
              value={form.noOfMonths ?? 1}
              inputProps={{ min: 1, step: 1 }}
              onChange={(e) => change({ noOfMonths: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberField
              label="Total No. of Classes"
              value={payload.totalNoOfClasses}
              inputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="secondary" display="block" mt={1}>
          Total classes are the sum of allocated classes across all tutors for the full package.
          Changing months does not change tutor allocations.
        </Typography>
      </Section>
      {form.parentId && !loading && !students.length && (
        <Alert severity="info">No students available for this parent.</Alert>
      )}
      {subjectState === "loading" && <Typography>Loading subjects…</Typography>}
      {subjectState === "error" && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry((v) => v + 1)}>Retry</Button>}
        >
          Could not load subjects.
        </Alert>
      )}
      {subjectState === "ready" && !subjects.length && (
        <Alert severity="info">No subjects available.</Alert>
      )}
      {form.tutorAssignments.map((row, index) => (
        <TutorRow
          key={row.localId}
          row={row}
          index={index}
          tutors={tutors}
          subjects={subjects}
          onChange={(patch) => updateRow(row.localId, patch)}
          onRemove={() =>
            change({
              tutorAssignments: form.tutorAssignments.filter((r) => r.localId !== row.localId),
            })
          }
        />
      ))}
      <Button
        variant="outlined"
        color="info"
        startIcon={<Add />}
        sx={{ mb: 3 }}
        onClick={() => change({ tutorAssignments: [...form.tutorAssignments, newAssignment()] })}
      >
        Add Tutor
      </Button>
      <Section
        title="Payment Information"
        description="Common student fees for this SME class"
        icon={<Payment />}
      >
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <NumberField
              label="Student package fee (₹)"
              value={form.paymentAmount}
              inputProps={{ min: 0 }}
              onChange={(e) => change({ paymentAmount: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="600" sx={{ py: 1 }}>
              Total allocated classes: {payload.totalNoOfClasses}
            </Typography>
            <Typography variant="caption" color="secondary">
              Combined total across all tutors
            </Typography>
          </Grid>
        </Grid>
      </Section>
      <Typography variant="caption" display="block" sx={{ mb: 2 }}>
        Schedule checks use available tutor data and this draft. Full student availability will be
        checked after integration.
      </Typography>
      {attempted && errors.length > 0 && (
        <Alert severity="error">
          <ul>
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Box>
  );
}
